// Firestore 유틸리티 함수
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { checkScheduleConflict } from './scheduleUtils';

// ========== 사용자 관리 ==========

/**
 * 사용자 프로필 조회
 */
export async function getUserProfile(uid) {
  const docRef = doc(db, 'users', uid);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
}

/**
 * 사용자 프로필 업데이트
 */
export async function updateUserProfile(uid, data) {
  const docRef = doc(db, 'users', uid);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now()
  });
}

/**
 * 모든 사용자 조회 (관리자용)
 */
export async function getAllUsers(filters = {}) {
  let q = collection(db, 'users');
  
  // role 필터
  if (filters.role) {
    q = query(q, where('role', '==', filters.role));
  }
  
  // status 필터
  if (filters.status) {
    q = query(q, where('status', '==', filters.status));
  }
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/**
 * 사용자 role 업데이트 (관리자용)
 */
export async function updateUserRole(uid, role) {
  const docRef = doc(db, 'users', uid);
  await updateDoc(docRef, {
    role,
    updatedAt: Timestamp.now()
  });
}

/**
 * 구인 풀 조회 (관리자용)
 */
export async function getCandidatePool(filters = {}) {
  let q = collection(db, 'users');
  
  // 기본적으로 candidate만 조회
  q = query(q, where('role', '==', 'candidate'));
  
  // 활성 상태만
  if (filters.activeOnly) {
    q = query(q, where('status', '==', 'active'));
  }
  
  // 사용자유형 필터
  if (filters.userType) {
    q = query(q, where('userType', '==', filters.userType));
  }
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/**
 * 전화번호 정규화 (하이픈 제거)
 */
function normalizePhoneNumber(phoneNumber) {
  if (!phoneNumber) return '';
  return phoneNumber.replace(/[-\s()]/g, '');
}

/**
 * 이름과 전화번호로 구인 풀에서 후보자 찾기
 * 전화번호는 하이픈 유무와 상관없이 매칭됨
 */
export async function findCandidateByNameAndPhone(name, phone) {
  try {
    // 입력된 전화번호 정규화
    const normalizedInputPhone = normalizePhoneNumber(phone);
    
    // 이름으로만 먼저 조회 (전화번호는 클라이언트 측에서 비교)
    const q = query(
      collection(db, 'users'),
      where('role', '==', 'candidate'),
      where('name', '==', name)
    );
    
    const snapshot = await getDocs(q);
    
    // 이름이 같은 후보자 중에서 전화번호가 일치하는 사람 찾기
    for (const doc of snapshot.docs) {
      const data = doc.data();
      const normalizedDbPhone = normalizePhoneNumber(data.phone);
      
      if (normalizedDbPhone === normalizedInputPhone) {
        return { id: doc.id, ...data };
      }
    }
    
    return null;
  } catch (error) {
    console.error('후보자 조회 오류:', error);
    return null;
  }
}

/**
 * 구인 풀 인원 추가/수정
 */
export async function saveCandidate(candidateId, candidateData) {
  if (candidateId) {
    // 수정
    const candidateRef = doc(db, 'users', candidateId);
    await updateDoc(candidateRef, {
      ...candidateData,
      updatedAt: Timestamp.now()
    });
    return candidateId;
  } else {
    // 추가
    const docRef = await addDoc(collection(db, 'users'), {
      ...candidateData,
      role: 'candidate',
      status: 'active',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return docRef.id;
  }
}

/**
 * 위치 목록 조회
 */
export async function getLocations() {
  try {
    const docRef = doc(db, 'settings', 'locations');
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data().list || ['장전캠퍼스', '양산캠퍼스', '밀양캠퍼스'];
    }
    return ['장전캠퍼스', '양산캠퍼스', '밀양캠퍼스']; // 기본값
  } catch (error) {
    console.error('위치 조회 오류:', error);
    return ['장전캠퍼스', '양산캠퍼스', '밀양캠퍼스'];
  }
}

/**
 * 위치 목록 저장
 */
export async function saveLocations(locations) {
  const docRef = doc(db, 'settings', 'locations');
  await setDoc(docRef, {
    list: locations,
    updatedAt: Timestamp.now()
  });
}

// ========== 구인 공고 관리 ==========

/**
 * 구인 공고 생성
 */
export async function createJobPosting(data, creatorUid) {
  const jobData = {
    ...data,
    createdBy: creatorUid,
    status: 'draft',
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  };
  
  const docRef = await addDoc(collection(db, 'job_postings'), jobData);
  return docRef.id;
}

/**
 * 구인 공고 조회
 */
export async function getJobPosting(jobId) {
  const docRef = doc(db, 'job_postings', jobId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
}

/**
 * 구인 공고 목록 조회
 */
export async function getJobPostings(filters = {}) {
  let q = collection(db, 'job_postings');
  
  // 열린 공고만
  if (filters.openOnly) {
    q = query(q, where('status', '==', 'open'));
  }
  
  // 위치 필터
  if (filters.location) {
    q = query(q, where('location', '==', filters.location));
  }
  
  // 정렬
  q = query(q, orderBy('createdAt', 'desc'));
  
  // 제한
  if (filters.limit) {
    q = query(q, limit(filters.limit));
  }
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/**
 * 구인 공고 업데이트
 */
export async function updateJobPosting(jobId, data) {
  const docRef = doc(db, 'job_postings', jobId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now()
  });
}

/**
 * 구인 공고 삭제
 */
export async function deleteJobPosting(jobId) {
  const docRef = doc(db, 'job_postings', jobId);
  await deleteDoc(docRef);
}

// ========== 신청 관리 ==========

/**
 * 신청서 제출 (중복 확인 포함)
 */
export async function submitApplication(jobId, userId, applicationNote = '') {
  // 중복 지원 확인
  const existingApps = await getJobApplications(jobId);
  const alreadyApplied = existingApps.find(
    app => app.userId === userId && (app.status === 'applied' || app.status === 'selected')
  );

  if (alreadyApplied) {
    throw new Error('이미 지원한 공고입니다.');
  }

  const applicationData = {
    jobId,
    userId,
    status: 'applied',
    appliedAt: Timestamp.now(),
    applicationNote: applicationNote || ''
  };
  
  const docRef = await addDoc(collection(db, 'applications'), applicationData);
  return docRef.id;
}

/**
 * 신청 조회
 */
export async function getApplication(applicationId) {
  const docRef = doc(db, 'applications', applicationId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
}

/**
 * 사용자의 신청 목록
 */
export async function getUserApplications(userId) {
  const q = query(
    collection(db, 'applications'),
    where('userId', '==', userId),
    orderBy('appliedAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/**
 * 공고의 신청 목록
 */
export async function getJobApplications(jobId) {
  const q = query(
    collection(db, 'applications'),
    where('jobId', '==', jobId),
    orderBy('appliedAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/**
 * 신청 상태 업데이트
 */
export async function updateApplicationStatus(applicationId, status, note = '') {
  const docRef = doc(db, 'applications', applicationId);
  const updateData = {
    status,
    updatedAt: Timestamp.now()
  };
  
  if (status === 'selected') {
    updateData.selectedAt = Timestamp.now();
  } else if (status === 'canceled') {
    updateData.canceledAt = Timestamp.now();
  }
  
  if (note) {
    updateData.selectionNote = note;
  }
  
  await updateDoc(docRef, updateData);
}

// ========== 선정 관리 ==========

/**
 * 선정 확정
 */
export async function createAssignment(jobId, userId, workDates, confirmedBy) {
  const assignmentData = {
    jobId,
    userId,
    workDates,
    confirmedBy,
    status: 'selected',
    confirmedAt: Timestamp.now(),
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  };
  
  const docRef = await addDoc(collection(db, 'assignments'), assignmentData);
  return docRef.id;
}

/**
 * 사용자의 근무 이력
 */
export async function getUserAssignments(userId) {
  const q = query(
    collection(db, 'assignments'),
    where('userId', '==', userId),
    orderBy('confirmedAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

// ========== 알림 관리 ==========

/**
 * 알림 큐에 추가
 */
export async function addNotificationToQueue(type, targetUserIds, payload) {
  const notificationData = {
    type,
    targetUserIds,
    payload,
    status: 'pending',
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  };
  
  const docRef = await addDoc(collection(db, 'notification_queue'), notificationData);
  return docRef.id;
}

/**
 * 알림 로그 조회
 */
export async function getNotificationLogs(limitCount = 50) {
  const q = query(
    collection(db, 'notification_logs'),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

// ========== 학기 정보 관리 (관리자) ==========

/**
 * 학기 생성
 */
export async function createSemester(semesterData) {
  // 다른 활성 학기가 있으면 비활성화
  if (semesterData.isActive) {
    const activeSemester = await getActiveSemester();
    if (activeSemester) {
      await updateSemester(activeSemester.id, { isActive: false });
    }
  }

  const data = {
    ...semesterData,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  };
  
  const docRef = await addDoc(collection(db, 'semesters'), data);
  return docRef.id;
}

/**
 * 활성 학기 조회
 */
export async function getActiveSemester() {
  const q = query(
    collection(db, 'semesters'),
    where('isActive', '==', true)
  );
  
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  
  const doc = snapshot.docs[0];
  return { id: doc.id, ...doc.data() };
}

/**
 * 모든 학기 조회
 */
export async function getAllSemesters() {
  const q = query(
    collection(db, 'semesters'),
    orderBy('startDate', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/**
 * 학기 업데이트
 */
export async function updateSemester(semesterId, updates) {
  const docRef = doc(db, 'semesters', semesterId);
  
  // isActive를 true로 변경하는 경우, 다른 활성 학기 비활성화
  if (updates.isActive === true) {
    const activeSemester = await getActiveSemester();
    if (activeSemester && activeSemester.id !== semesterId) {
      await updateDoc(doc(db, 'semesters', activeSemester.id), { isActive: false });
    }
  }
  
  await updateDoc(docRef, {
    ...updates,
    updatedAt: Timestamp.now()
  });
}

/**
 * 학기 삭제
 */
export async function deleteSemester(semesterId) {
  const docRef = doc(db, 'semesters', semesterId);
  await deleteDoc(docRef);
}

/**
 * 입력 가능한 학기인지 확인
 */
export function canRegisterSchedule(semester) {
  if (!semester) return false;
  
  const today = new Date();
  const startDate = new Date(semester.registrationStartDate);
  const endDate = new Date(semester.registrationEndDate);
  
  return today >= startDate && today <= endDate;
}

// ========== 학업시간표 관리 ==========

/**
 * 학업시간표 생성
 */
export async function createAcademicSchedule(userId, scheduleData) {
  // 다른 활성 학기가 있으면 비활성화
  if (scheduleData.isActive) {
    const activeSchedules = await getActiveAcademicSchedule(userId);
    if (activeSchedules) {
      await updateAcademicSchedule(activeSchedules.id, { isActive: false });
    }
  }

  const data = {
    userId,
    ...scheduleData,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  };
  
  const docRef = await addDoc(collection(db, 'academic_schedules'), data);
  return docRef.id;
}

/**
 * 활성 학업시간표 조회
 */
export async function getActiveAcademicSchedule(userId) {
  const q = query(
    collection(db, 'academic_schedules'),
    where('userId', '==', userId),
    where('isActive', '==', true)
  );
  
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  
  const doc = snapshot.docs[0];
  return { id: doc.id, ...doc.data() };
}

/**
 * 사용자의 모든 학업시간표 조회
 */
export async function getAcademicSchedules(userId) {
  const q = query(
    collection(db, 'academic_schedules'),
    where('userId', '==', userId),
    orderBy('startDate', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/**
 * 학업시간표 업데이트
 */
export async function updateAcademicSchedule(scheduleId, updates) {
  const docRef = doc(db, 'academic_schedules', scheduleId);
  
  // isActive를 true로 변경하는 경우, 다른 활성 학기 비활성화
  if (updates.isActive === true) {
    const scheduleSnap = await getDoc(docRef);
    if (scheduleSnap.exists()) {
      const userId = scheduleSnap.data().userId;
      const activeSchedule = await getActiveAcademicSchedule(userId);
      if (activeSchedule && activeSchedule.id !== scheduleId) {
        await updateDoc(doc(db, 'academic_schedules', activeSchedule.id), { isActive: false });
      }
    }
  }
  
  await updateDoc(docRef, {
    ...updates,
    updatedAt: Timestamp.now()
  });
}

/**
 * 학업시간표 삭제
 */
export async function deleteAcademicSchedule(scheduleId) {
  const docRef = doc(db, 'academic_schedules', scheduleId);
  await deleteDoc(docRef);
}

// ========== 근무스케줄 슬롯 관리 ==========

/**
 * 근무스케줄 슬롯 생성
 */
export async function createWorkSchedule(scheduleData, creatorUid) {
  const data = {
    ...scheduleData,
    confirmedCount: 0,
    status: 'open',
    createdBy: creatorUid,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  };
  
  const docRef = await addDoc(collection(db, 'work_schedules'), data);
  return docRef.id;
}

/**
 * 근무스케줄 슬롯 조회 (단일)
 */
export async function getWorkSchedule(scheduleId) {
  const docRef = doc(db, 'work_schedules', scheduleId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
}

/**
 * 근무스케줄 슬롯 목록 조회
 * 색인 문제를 피하기 위해 학기만 서버 필터링, 나머지는 클라이언트 필터링
 */
export async function getWorkSchedules(filters = {}) {
  let q = collection(db, 'work_schedules');
  
  // 학기 필터만 서버 측에서 처리 (단일 필드 색인만 필요)
  if (filters.semesterId) {
    q = query(q, where('semesterId', '==', filters.semesterId));
  }
  
  const snapshot = await getDocs(q);
  let schedules = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  
  // semesterId가 없는 기존 스케줄 처리 (fallback)
  // semesterId 필터를 요청했지만 결과가 0개인 경우, semesterId가 없는 스케줄도 조회
  if (filters.semesterId && schedules.length === 0) {
    const allSnapshot = await getDocs(collection(db, 'work_schedules'));
    const allSchedules = allSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // semesterId가 없는 스케줄만 필터링 (날짜 범위로 판단)
    if (filters.startDate && filters.endDate) {
      schedules = allSchedules.filter(s => 
        !s.semesterId && 
        s.workDate >= filters.startDate && 
        s.workDate <= filters.endDate
      );
    }
  }
  
  // 클라이언트 측 필터링
  if (filters.status) {
    schedules = schedules.filter(s => s.status === filters.status);
  }
  
  if (filters.location) {
    schedules = schedules.filter(s => s.location === filters.location);
  }
  
  if (filters.startDate) {
    schedules = schedules.filter(s => s.workDate >= filters.startDate);
  }
  
  if (filters.endDate) {
    schedules = schedules.filter(s => s.workDate <= filters.endDate);
  }
  
  // 클라이언트 측 정렬
  schedules.sort((a, b) => {
    if (a.workDate < b.workDate) return -1;
    if (a.workDate > b.workDate) return 1;
    return 0;
  });
  
  return schedules;
}

/**
 * 근무스케줄 슬롯 업데이트
 */
export async function updateWorkSchedule(scheduleId, updates) {
  const docRef = doc(db, 'work_schedules', scheduleId);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: Timestamp.now()
  });
}

/**
 * 근무스케줄 슬롯 삭제
 */
export async function deleteWorkSchedule(scheduleId) {
  const docRef = doc(db, 'work_schedules', scheduleId);
  await deleteDoc(docRef);
}

// ========== 스케줄 신청 관리 ==========

/**
 * 스케줄 신청 (충돌 체크 포함)
 */
export async function applyForSchedule(userId, scheduleId, note = '') {
  // 중복 신청 확인
  const existingApps = await getScheduleApplications(scheduleId);
  const alreadyApplied = existingApps.find(
    app => app.userId === userId && ['pending', 'selected'].includes(app.status)
  );

  if (alreadyApplied) {
    throw new Error('이미 신청한 스케줄입니다.');
  }

  // 스케줄 정보 조회
  const schedule = await getWorkSchedule(scheduleId);
  if (!schedule) {
    throw new Error('존재하지 않는 스케줄입니다.');
  }

  if (schedule.status !== 'open') {
    throw new Error('신청할 수 없는 스케줄입니다.');
  }

  const applicationData = {
    scheduleId,
    userId,
    status: 'pending',
    appliedAt: Timestamp.now(),
    note: note || '',
    conflictWarning: false
  };
  
  const docRef = await addDoc(collection(db, 'schedule_applications'), applicationData);
  return docRef.id;
}

/**
 * 충돌 체크 후 스케줄 신청
 */
export async function checkConflictAndApply(userId, scheduleId, note = '') {
  // 활성 학업시간표 조회
  const academicSchedule = await getActiveAcademicSchedule(userId);
  
  // 스케줄 정보 조회
  const workSchedule = await getWorkSchedule(scheduleId);
  
  if (!workSchedule) {
    throw new Error('존재하지 않는 스케줄입니다.');
  }

  // 충돌 체크
  if (academicSchedule) {
    const conflictResult = checkScheduleConflict(academicSchedule, workSchedule);
    
    if (conflictResult.hasConflict) {
      const subjects = conflictResult.conflictingClasses.map(c => c.subject).join(', ');
      throw new Error(`학업시간과 겹칩니다: ${subjects}`);
    }
  }

  // 충돌 없으면 신청 진행
  return await applyForSchedule(userId, scheduleId, note);
}

/**
 * 스케줄 신청 취소
 */
export async function cancelScheduleApplication(applicationId) {
  const docRef = doc(db, 'schedule_applications', applicationId);
  await updateDoc(docRef, {
    status: 'canceled',
    canceledAt: Timestamp.now()
  });
}

/**
 * 스케줄별 신청 목록 조회
 */
export async function getScheduleApplications(scheduleId, statusFilter = null) {
  let q = query(
    collection(db, 'schedule_applications'),
    where('scheduleId', '==', scheduleId)
  );
  
  if (statusFilter) {
    q = query(q, where('status', '==', statusFilter));
  }
  
  q = query(q, orderBy('appliedAt', 'asc'));
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/**
 * 사용자의 스케줄 신청 목록 조회
 */
export async function getUserScheduleApplications(userId) {
  const q = query(
    collection(db, 'schedule_applications'),
    where('userId', '==', userId),
    orderBy('appliedAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  const applications = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  
  // 각 신청에 대한 스케줄 정보 가져오기
  const applicationsWithDetails = await Promise.all(
    applications.map(async (app) => {
      const schedule = await getWorkSchedule(app.scheduleId);
      return {
        ...app,
        scheduleDetails: schedule
      };
    })
  );
  
  return applicationsWithDetails;
}

/**
 * 신청자 선정 (assignments 생성 포함)
 */
export async function selectApplicant(applicationId, scheduleId, userId, adminUid) {
  const batch = writeBatch(db);

  // 1. 신청 상태 업데이트
  const appRef = doc(db, 'schedule_applications', applicationId);
  batch.update(appRef, {
    status: 'selected',
    selectedAt: Timestamp.now()
  });

  // 2. 스케줄 정보 조회
  const schedule = await getWorkSchedule(scheduleId);
  if (!schedule) {
    throw new Error('스케줄을 찾을 수 없습니다.');
  }

  // 3. assignments 생성
  const assignmentRef = doc(collection(db, 'assignments'));
  batch.set(assignmentRef, {
    jobId: scheduleId, // 호환성을 위해 jobId 필드 사용
    userId,
    workDates: [schedule.workDate],
    workHours: {
      start: schedule.startTime,
      end: schedule.endTime
    },
    confirmedBy: adminUid,
    confirmedAt: Timestamp.now(),
    status: 'confirmed',
    actualWage: schedule.wageAmount,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });

  // 4. 스케줄의 확정 인원 증가
  const scheduleRef = doc(db, 'work_schedules', scheduleId);
  const newConfirmedCount = (schedule.confirmedCount || 0) + 1;
  const updates = {
    confirmedCount: newConfirmedCount,
    updatedAt: Timestamp.now()
  };

  batch.update(scheduleRef, updates);

  await batch.commit();

  // 5. 근무자 시간 업데이트 (활성 학기 자동 조회)
  try {
    const activeSemester = await getActiveSemester();
    if (activeSemester) {
      await updateWorkerHours(userId, activeSemester.id);
    }
  } catch (error) {
    console.error('근무자 시간 업데이트 오류:', error);
  }

  return assignmentRef.id;
}

/**
 * 근무자 직접 할당 (신청 없이 관리자가 직접 배정)
 */
export async function assignWorkerDirectly(scheduleId, userId, adminUid, semesterId = null) {
  const batch = writeBatch(db);

  // 1. 스케줄 정보 조회
  const schedule = await getWorkSchedule(scheduleId);
  if (!schedule) {
    throw new Error('스케줄을 찾을 수 없습니다.');
  }

  // 2. 신청서 생성 (관리자가 직접 배정)
  const appRef = doc(collection(db, 'schedule_applications'));
  batch.set(appRef, {
    scheduleId,
    userId,
    status: 'selected',
    appliedAt: Timestamp.now(),
    selectedAt: Timestamp.now(),
    note: '관리자 직접 배정',
    directAssignment: true
  });

  // 3. assignments 생성
  const assignmentRef = doc(collection(db, 'assignments'));
  batch.set(assignmentRef, {
    jobId: scheduleId,
    userId,
    workDates: [schedule.workDate],
    workHours: {
      start: schedule.startTime,
      end: schedule.endTime
    },
    confirmedBy: adminUid,
    confirmedAt: Timestamp.now(),
    status: 'confirmed',
    actualWage: schedule.wageAmount,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });

  // 4. 스케줄의 확정 인원 증가
  const scheduleRef = doc(db, 'work_schedules', scheduleId);
  const newConfirmedCount = (schedule.confirmedCount || 0) + 1;
  const updates = {
    confirmedCount: newConfirmedCount,
    updatedAt: Timestamp.now()
  };

  batch.update(scheduleRef, updates);

  await batch.commit();

  // 5. 근무자 시간 업데이트 (batch 외부에서 처리)
  if (semesterId) {
    try {
      await updateWorkerHours(userId, semesterId);
    } catch (error) {
      console.error('근무자 시간 업데이트 오류:', error);
    }
  }

  return { applicationId: appRef.id, assignmentId: assignmentRef.id };
}

/**
 * 신청자 거절
 */
export async function rejectApplicant(applicationId, reason = '') {
  const docRef = doc(db, 'schedule_applications', applicationId);
  await updateDoc(docRef, {
    status: 'rejected',
    rejectedAt: Timestamp.now(),
    adminNote: reason
  });
}

/**
 * 선정된 신청자 삭제 (배정 취소)
 */
export async function cancelSelectedApplicant(applicationId, scheduleId) {
  // 신청 정보 조회 (userId 가져오기 위함)
  const appDoc = await getDoc(doc(db, 'schedule_applications', applicationId));
  if (!appDoc.exists()) {
    throw new Error('신청 정보를 찾을 수 없습니다.');
  }
  const userId = appDoc.data().userId;

  const batch = writeBatch(db);

  // 1. 신청 상태를 canceled로 변경
  const appRef = doc(db, 'schedule_applications', applicationId);
  batch.update(appRef, {
    status: 'canceled',
    canceledAt: Timestamp.now()
  });

  // 2. 스케줄의 확정 인원 감소
  const scheduleRef = doc(db, 'work_schedules', scheduleId);
  const schedule = await getWorkSchedule(scheduleId);
  if (schedule) {
    const newConfirmedCount = Math.max(0, (schedule.confirmedCount || 0) - 1);
    batch.update(scheduleRef, {
      confirmedCount: newConfirmedCount,
      updatedAt: Timestamp.now()
    });
  }

  await batch.commit();

  // 3. 근무자 시간 업데이트
  try {
    const activeSemester = await getActiveSemester();
    if (activeSemester) {
      await updateWorkerHours(userId, activeSemester.id);
    }
  } catch (error) {
    console.error('근무자 시간 업데이트 오류:', error);
  }
}

/**
 * 사용자의 확정된 스케줄 조회
 */
export async function getUserConfirmedSchedules(userId) {
  // schedule_applications에서 selected 상태인 것들 조회
  const q = query(
    collection(db, 'schedule_applications'),
    where('userId', '==', userId),
    where('status', '==', 'selected'),
    orderBy('appliedAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  const applications = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  
  // 각 신청에 대한 스케줄 정보 가져오기
  const schedulesWithDetails = await Promise.all(
    applications.map(async (app) => {
      const schedule = await getWorkSchedule(app.scheduleId);
      return {
        ...app,
        scheduleDetails: schedule
      };
    })
  );
  
  return schedulesWithDetails;
}

// ==================== Semester Workers ====================

/**
 * 학기별 근무자 추가
 */
export async function addSemesterWorker(semesterId, userId, userData, maxWorkHours) {
  // 중복 체크
  const q = query(
    collection(db, 'semester_workers'),
    where('semesterId', '==', semesterId),
    where('userId', '==', userId)
  );
  const existing = await getDocs(q);
  
  if (!existing.empty) {
    throw new Error('이미 추가된 근무자입니다.');
  }

  const docRef = await addDoc(collection(db, 'semester_workers'), {
    semesterId,
    userId,
    userName: userData.name || '',
    userEmail: userData.email || '',
    userPhone: userData.phone || '',
    maxWorkHours,
    appliedHours: 0,
    completedHours: 0,
    remainingHours: maxWorkHours,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });

  // 사용자에게 canUseSchedule 권한 부여
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    canUseSchedule: true,
    updatedAt: Timestamp.now()
  });

  return docRef.id;
}

/**
 * 학기별 근무자 일괄 추가
 */
export async function addSemesterWorkersBatch(semesterId, workers) {
  const batch = writeBatch(db);
  const workerRefs = [];

  for (const worker of workers) {
    // 중복 체크
    const q = query(
      collection(db, 'semester_workers'),
      where('semesterId', '==', semesterId),
      where('userId', '==', worker.userId)
    );
    const existing = await getDocs(q);
    
    if (existing.empty) {
      const workerRef = doc(collection(db, 'semester_workers'));
      batch.set(workerRef, {
        semesterId,
        userId: worker.userId,
        userName: worker.name || '',
        userEmail: worker.email || '',
        userPhone: worker.phone || '',
        maxWorkHours: worker.maxWorkHours,
        appliedHours: 0,
        completedHours: 0,
        remainingHours: worker.maxWorkHours,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      // 사용자에게 canUseSchedule 권한 부여
      const userRef = doc(db, 'users', worker.userId);
      batch.update(userRef, {
        canUseSchedule: true,
        updatedAt: Timestamp.now()
      });

      workerRefs.push(workerRef.id);
    }
  }

  await batch.commit();
  return workerRefs;
}

/**
 * 학기별 근무자 제거
 */
export async function removeSemesterWorker(workerId) {
  // 근무자 정보 조회
  const workerRef = doc(db, 'semester_workers', workerId);
  const workerSnap = await getDoc(workerRef);
  
  if (!workerSnap.exists()) {
    throw new Error('근무자 정보를 찾을 수 없습니다.');
  }

  const workerData = workerSnap.data();
  const userId = workerData.userId;

  // 삭제
  await deleteDoc(workerRef);

  // 해당 사용자가 다른 학기의 근무자인지 확인
  const q = query(
    collection(db, 'semester_workers'),
    where('userId', '==', userId)
  );
  const remainingWorkers = await getDocs(q);

  // 다른 학기에도 속하지 않으면 canUseSchedule 권한 제거
  if (remainingWorkers.empty) {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      canUseSchedule: false,
      updatedAt: Timestamp.now()
    });
  }
}

/**
 * 학기별 근무자 일괄 제거
 */
export async function removeSemesterWorkersBatch(workerIds) {
  const batch = writeBatch(db);
  
  workerIds.forEach(id => {
    batch.delete(doc(db, 'semester_workers', id));
  });

  await batch.commit();
}

/**
 * 학기별 근무자 목록 조회
 */
export async function getSemesterWorkers(semesterId) {
  const q = query(
    collection(db, 'semester_workers'),
    where('semesterId', '==', semesterId),
    orderBy('createdAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/**
 * 사용자의 근무자 정보 조회 (모든 학기)
 */
export async function getUserSemesterWorkers(userId) {
  const q = query(
    collection(db, 'semester_workers'),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/**
 * 특정 근무자 정보 조회
 */
export async function getSemesterWorker(workerId) {
  const docRef = doc(db, 'semester_workers', workerId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    throw new Error('근무자 정보를 찾을 수 없습니다.');
  }
  
  return { id: docSnap.id, ...docSnap.data() };
}

/**
 * 근무자의 최대 근로시간 업데이트
 */
export async function updateWorkerMaxHours(workerId, maxWorkHours) {
  const docRef = doc(db, 'semester_workers', workerId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    throw new Error('근무자 정보를 찾을 수 없습니다.');
  }

  const data = docSnap.data();
  const remainingHours = maxWorkHours - data.appliedHours - data.completedHours;

  await updateDoc(docRef, {
    maxWorkHours,
    remainingHours,
    updatedAt: Timestamp.now()
  });
}

/**
 * 근무자의 근로시간 통계 업데이트 (스케줄 신청/완료 시 호출)
 */
export async function updateWorkerHours(userId, semesterId) {
  // 해당 학기의 근무자 정보 찾기
  const q = query(
    collection(db, 'semester_workers'),
    where('semesterId', '==', semesterId),
    where('userId', '==', userId)
  );
  
  const snapshot = await getDocs(q);
  if (snapshot.empty) return;

  const workerDoc = snapshot.docs[0];
  const workerData = workerDoc.data();

  // 해당 학기 내에서 사용자의 스케줄 신청 조회
  const appsQuery = query(
    collection(db, 'schedule_applications'),
    where('userId', '==', userId)
  );
  const appsSnapshot = await getDocs(appsQuery);
  
  let appliedHours = 0;
  let completedHours = 0;

  for (const appDoc of appsSnapshot.docs) {
    const app = appDoc.data();
    if (app.status === 'pending' || app.status === 'selected') {
      // 스케줄 정보 가져오기
      const schedule = await getWorkSchedule(app.scheduleId);
      if (schedule) {
        const [startH, startM] = schedule.startTime.split(':').map(Number);
        const [endH, endM] = schedule.endTime.split(':').map(Number);
        const hours = ((endH * 60 + endM) - (startH * 60 + startM)) / 60;
        
        if (app.status === 'pending') {
          appliedHours += hours;
        } else if (app.status === 'selected' && schedule.status === 'completed') {
          completedHours += hours;
        } else if (app.status === 'selected') {
          appliedHours += hours;
        }
      }
    }
  }

  const remainingHours = workerData.maxWorkHours - appliedHours - completedHours;

  await updateDoc(doc(db, 'semester_workers', workerDoc.id), {
    appliedHours,
    completedHours,
    remainingHours,
    updatedAt: Timestamp.now()
  });
}
